// Top-up packs on the Billing page, for a customer the token ledger is switched on for:
// what top-ups are left, what their plan does when they run out (no hidden limits), and
// the packs their plan sells (docs/planning/TOKEN_LEDGER_PLAN.md §6.4, 10.10).

import { useMemo, useState } from 'react';
import { PACK_CURRENCIES, type OutOfTokensPolicy, type TokenPack } from '../admin/PackageTokenSettings';
import type { TokenRunOut } from '../../types/subscription';

type Currency = (typeof PACK_CURRENCIES)[number];
const SYMBOL: Record<Currency, string> = { NZD: 'NZ$', USD: 'US$', EUR: '€', GBP: '£', AUD: 'A$' };

const FLOOR = 'Charts already on your boat always draw.';

/** The plan's out-of-tokens rule, as the skipper reads it. */
export function runOutRule(policy: OutOfTokensPolicy | null, amount: number | null): string {
  const n = (amount ?? 0).toLocaleString('en-NZ');
  switch (policy) {
    case 'stop': return `New downloads and paid live charts stop until you top up. ${FLOOR}`;
    case 'overdraft': return `You can carry on for up to ${n} tokens more; they come out of your next allowance or top-up. ${FLOOR}`;
    case 'grace': return `You can carry on for ${n} days, to top up when you are back in signal. ${FLOOR}`;
    case 'live_keeps_drawing': return `Live charts carry on; only new downloads stop until you top up. ${FLOOR}`;
    default: return `Downloads carry on; anything over your balance is recorded. ${FLOOR}`;
  }
}

function price(cents: number, cur: Currency): string {
  return `${SYMBOL[cur]}${(cents / 100).toFixed(2)}`;
}

/** Where the customer stands under the rule now, under the rule's words. */
function standing(r: TokenRunOut | undefined): string | null {
  if (!r) return null;
  if (r.policy === 'overdraft' && r.overdraft_left != null && r.amount != null) {
    return `${r.overdraft_left.toLocaleString('en-NZ')} of ${r.amount.toLocaleString('en-NZ')} overdraft tokens left.`;
  }
  if (r.policy === 'grace' && r.grace_ends_at) {
    return `Live charts keep drawing until ${new Date(r.grace_ends_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}.`;
  }
  return null;
}

export function TopUpCard({ packs, topupRemaining, policy, amount, onBuy, buying, runOut, error }: {
  packs: TokenPack[];
  topupRemaining: number;
  policy: OutOfTokensPolicy | null;
  amount: number | null;
  onBuy: (packId: string, currency: Currency) => void;
  /** The pack whose payment is opening, if any. */
  buying: string | null;
  runOut?: TokenRunOut;
  /** Why the last purchase did not start; stays until the next try. */
  error?: string | null;
}) {
  const onSale = useMemo(() => packs.filter((p) => p.on_sale), [packs]);
  const currencies = PACK_CURRENCIES.filter((c) => onSale.some((p) => p.prices[c]));
  const [currency, setCurrency] = useState<Currency>(currencies.includes('NZD') ? 'NZD' : currencies[0] ?? 'NZD');
  const shown = onSale.filter((p) => p.prices[currency]);

  return (
    <div className="mt-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
          <p className="text-sm text-slate-400 mb-1">Top-ups left</p>
          <p className="text-xl font-bold text-white">{topupRemaining.toLocaleString('en-NZ')}</p>
          <p className="text-sm text-slate-400 mt-1">Never expire while you are subscribed</p>
        </div>
        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
          <p className="text-sm text-slate-400 mb-1">When you run out</p>
          <p className="text-sm text-white">{runOutRule(policy, amount)}</p>
          {standing(runOut) && <p className="text-sm text-cyan-400 mt-2">{standing(runOut)}</p>}
        </div>
      </div>

      <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-medium text-white">Buy tokens</h3>
          {currencies.length > 1 && (
            <label className="text-sm text-slate-300 flex items-center gap-2">
              Currency
              <select className="bg-slate-900 border border-slate-600 rounded-lg text-white text-sm px-2 py-1"
                value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          )}
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">{error}</p>
        )}
        {shown.length === 0 ? (
          <p className="text-sm text-slate-400">Your plan has no top-up packs on sale.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {shown.map((p) => {
              const label = `Buy ${p.tokens.toLocaleString('en-NZ')} tokens for ${price(p.prices[currency]!, currency)}`;
              return (
                <button key={p.id} type="button" aria-label={label} disabled={buying != null}
                  onClick={() => onBuy(p.id, currency)}
                  className="p-4 rounded-xl border border-cyan-500/40 hover:border-cyan-400 text-left disabled:opacity-60">
                  <span className="block text-lg font-bold text-white">{p.tokens.toLocaleString('en-NZ')} tokens</span>
                  <span className="block text-sm text-cyan-400">{buying === p.id ? 'Opening payment…' : price(p.prices[currency]!, currency)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
